import { useEffect } from "react";
import type IJitsiMeetExternalApi from "@jitsi/react-sdk/lib/types/IJitsiMeetExternalApi";
import { useCallState } from "@/zustand/CallState";
import { ORION_CHAT_TOOLBAR_BUTTON_ID } from "./jitsiToolbar";

// Re-exported so existing importers of this ID keep working; the source of truth now lives in
// `jitsiToolbar.ts` alongside the `customToolbarButtons` config that emits the click.
export { ORION_CHAT_TOOLBAR_BUTTON_ID };

/**
 * Payload of Jitsi's `toolbarButtonClicked` event, fired for buttons added via the iframe
 * `configOverwrite.customToolbarButtons` list. The `key` field carries the button id — confirmed
 * against the deployed bundle (build v=9268, which emits `toolbar-button-clicked` with `key`). We
 * still read `id`/`buttonId` defensively (they have appeared across jitsi-meet versions) and ignore
 * anything that isn't the Orion chat button.
 */
type ToolbarButtonClickedEvent = {
  key?: string;
  id?: string;
  buttonId?: string;
};

/**
 * Wires the Jitsi iframe's `toolbarButtonClicked` event to the Orion chat side panel.
 *
 * The "Chat" button is injected via the iframe `configOverwrite.customToolbarButtons` in
 * `Call.tsx` (see `buildOrionCustomToolbarButtons`) — this is supported through the iframe config
 * (verified against the deployed build; the earlier jitsi-meet#13769 "silently ignored" belief was
 * stale). When a user clicks it, Jitsi emits `toolbarButtonClicked` with the button's `key`, and
 * this listener toggles the Orion chat panel.
 *
 * Reads `useCallState.getState()` at call time (same pattern as `useModerationEvents`) so the
 * listener never captures a stale action reference across renders.
 */
export default function useOrionChatButtonEvent(
  apiRef: IJitsiMeetExternalApi | null,
): void {
  useEffect(() => {
    if (!apiRef) {
      return;
    }

    function handleToolbarButtonClicked(payload?: ToolbarButtonClickedEvent) {
      const clickedId = payload?.key ?? payload?.id ?? payload?.buttonId;
      if (clickedId !== ORION_CHAT_TOOLBAR_BUTTON_ID) {
        return;
      }
      useCallState.getState().toggleOrionChatPanel();
    }

    apiRef.on("toolbarButtonClicked", handleToolbarButtonClicked);

    return () => {
      apiRef.removeListener("toolbarButtonClicked", handleToolbarButtonClicked);
    };
  }, [apiRef]);
}
